import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  AddToGroupClickBiEvent,
  BiContextProvidedValue,
  BiData,
  PopupClickAddToPrivateCollectionBiEvent,
} from '@wix/da-bi/pkg/events';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import {
  StandardDeviationElementPlacement,
  StandardDeviationElementPlacementMap,
  StandardDeviationElementPlacementMultipleAtOnce,
} from '../../ElementPlacementContext/variants/standard/types';
import { StandardDeviationElementPlacementContext } from '../../ElementPlacementContext/variants/standard/context';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import { usePopover } from '../../../Popovers/redux/hooks';
import { PopoverType } from '../../../Popovers/redux/types';
import AddToGroupPopover from '../../_partials/AddToGroupPopup';
import { ActionBarContext } from '../../ActionBarContext';
import AddToCollectionPopup from '../../_partials/AddToCollectionPopup';
import { getDeviationNotificationQueue } from '../../../DeviationPage/redux/helpers';
import { NotificationType } from '../../../Notifications';
import {
  pushAddToGroupModal,
  pushPrivateCollectionModal,
} from '../../../Modals/redux/actionCreators';

/**
 * An action that requires a popup to be displayed. Contains the dropdown entry ({@link action}) and
 * the rendered popup ({@link popup}). Also, an optional {@link notificationType} for those popups that may need to
 * show a notification on some user action being performed in the attached popup.
 */
export interface MoreMenuPopupAction {
  /**
   * The rendered popup. This handles its own visibility, so you can output this without fear of it showing
   * when you wouldn't want it to.
   */
  popup: React.ReactNode;
  /**
   * The dropdown entry you need to pass the MoreMenu
   */
  action: DropdownOption;
  /**
   * Used if the popup has some action that requires a notification to be displayed
   */
  notificationType?: NotificationType;
}

/**
 * A helper for our elements with multiple placements. Since they could be an array or a single {@link StandardDeviationElementPlacement}
 * value, we have to check for that.
 */
function isElementInMoreMenu(
  elementPlacementValue: StandardDeviationElementPlacementMultipleAtOnce
) {
  if (elementPlacementValue === StandardDeviationElementPlacement.NotIncluded) {
    return false;
  }
  return (
    elementPlacementValue.includes(StandardDeviationElementPlacement.PrimaryBarMenu) ||
    elementPlacementValue.includes(StandardDeviationElementPlacement.SecondaryBarMenu)
  );
}

/**
 * Popup action hooks can return the requisite action or `undefined` if the current {@link StandardDeviationElementPlacementMap}
 * doesn't have said action set to appear in the more menu.
 */
type PopupActionHookReturnType = MoreMenuPopupAction | undefined;

function usePrivateCollectionPopupAction(deviation: PapiDeviation): PopupActionHookReturnType {
  const { t } = useTranslation();
  const dispatchPushPrivateCollectionModal = useAction(pushPrivateCollectionModal);
  const elementPlacementMap = useContext(StandardDeviationElementPlacementContext);
  const actionBarType = useContext(ActionBarContext);
  const isMobile = useContext(MobileContext);
  const { popover, removePopover, togglePopover } = usePopover(
    actionBarType === 'primary'
      ? PopoverType.ADD_TO_PRIVATE_COLLECTION_PRIMARY
      : PopoverType.ADD_TO_PRIVATE_COLLECTION_SECONDARY
  );

  const notificationQueue = isMobile ? 'default' : getDeviationNotificationQueue(deviation);

  const handleClick = useCallback(() => {
    if (isMobile) {
      dispatchPushPrivateCollectionModal(
        deviation,
        NotificationType.CollectPrivate,
        popover.params?.ignorePreselectedCollections
      );
    } else {
      togglePopover();
    }
  }, [dispatchPushPrivateCollectionModal, popover.params, togglePopover, isMobile, deviation]);

  if (elementPlacementMap.privateCollection !== StandardDeviationElementPlacement.PrimaryBarMenu) {
    return undefined;
  }

  return {
    // Mobile is handled by our usual modal systems
    popup: !isMobile && (
      <PositionOnlyPopperPopover
        key="private-collection"
        popoverContent={
          <AddToCollectionPopup
            isPrivateCollections
            collectibleDeviation={deviation}
            notificationQueue={notificationQueue}
            ignorePreselectedCollections={popover.params?.ignorePreselectedCollections}
            onClose={removePopover}
          />
        }
        offsetModifier={[0, 20]}
        isVisible={popover.isOpen}
        placement="bottom"
        onClickOutside={isMobile ? undefined : removePopover}
      />
    ),
    action: {
      value: 'collectPrivately',
      label: t('actionbar.collectPrivately.label'),
      onClick: handleClick,
      biData: BiData<PopupClickAddToPrivateCollectionBiEvent>({
        evid: 334,
        creatorid: BiContextProvidedValue,
        sectionname: BiContextProvidedValue,
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
      }),
    },
    notificationType: NotificationType.CollectPrivate,
  };
}

function useAddToGroupPopupAction(deviation: PapiDeviation, idSuffix = ''): PopupActionHookReturnType {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatchPushAddToGroupModal = useAction(pushAddToGroupModal);
  const actionBarType = useContext(ActionBarContext);
  const elementPlacementMap = useContext(StandardDeviationElementPlacementContext);

  const {
    popover: addToGroupPopover,
    pushPopover: pushAddToGroupPopover,
    removePopover: removeAddToGroupPopover,
  } = usePopover(
    // we add an idSuffix to differentiate this popup (and its isOpen state)
    // from any others (i.e. the same popup with the same PopoverType that
    // the action bar icon button opens), otherwise, opening this popover
    // will open those
    `${actionBarType === 'primary'
      ? PopoverType.ADD_TO_GROUP_PRIMARY
      : PopoverType.ADD_TO_GROUP_SECONDARY}${idSuffix}`
  );
  const handleClick = useCallback(() => {
    if (isMobile) {
      dispatchPushAddToGroupModal(deviation, undefined);
    } else {
      pushAddToGroupPopover();
    }
  }, [isMobile, dispatchPushAddToGroupModal, pushAddToGroupPopover, deviation]);

  return isElementInMoreMenu(elementPlacementMap.addToGroup)
    ? {
      popup: !isMobile && (
        <PositionOnlyPopperPopover
          key="add-to-group-popover"
          popoverContent={
            <AddToGroupPopover
              actionbarType={actionBarType}
              deviation={deviation}
              onClose={removeAddToGroupPopover}
            />
          }
          isVisible={addToGroupPopover.isOpen}
          placement="bottom"
          onClickOutside={removeAddToGroupPopover}
        />
      ),
      action: {
        value: 'addToGroup',
        label: t('actionbar.addToGroup'),
        onClick: handleClick,
        biData: BiData<AddToGroupClickBiEvent>({
          evid: 340,
          deviationid: deviation.deviationId,
          sectionname: 'add_to_group',
        }),
      },
      notificationType: NotificationType.AddToGroup,
    }
    : undefined;
}

/**
 * Full list of our {@link StandardDeviationElementPlacementMap} keys that we want in our more menu that trigger their
 * own popups. This is for type safety.
 */
type ElementKeys = Extract<
  keyof StandardDeviationElementPlacementMap,
  'addToGroup' | 'privateCollection'
>;

export type UseActionsWithPopupsReturnType = {
  [key in ElementKeys]: MoreMenuPopupAction | undefined;
};

/**
 * Actions that require a popup to be displayed. Maps from our {@link StandardDeviationElementPlacementMap} keys to a
 * {@link MoreMenuPopupAction} or `undefined`. `undefined` means the action was set to appear elsewhere or nowhere.
 */
export function useActionsWithPopups(deviation): UseActionsWithPopupsReturnType {
  return {
    addToGroup: useAddToGroupPopupAction(deviation, '-moreButton'),
    privateCollection: usePrivateCollectionPopupAction(deviation),
  };
}
